import { type Token, TokenKind } from '../../json/tokens';
import { scanStructure, structuralTokens, textOf } from '../support';
import type { RepairContext, RepairProposal, RepairRule } from '../types';

/**
 * `NaN`, `Infinity`, `-Infinity` → `null`.
 *
 * **Heuristic on purpose** (ADR-0012). JSON cannot represent any of the three, so *something*
 * has to give, but which thing is a question about the data: the author may have meant `0`,
 * the string `"NaN"`, a missing measurement, or an upstream bug worth seeing. `null` is the
 * most common intent, not the provable one, so the user says yes to it rather than having it
 * ride along with "Fix all".
 *
 * The legacy pipeline made exactly the choice this classification forbids: its unquoted-value
 * regex rewrote `{"a":NaN}` to `{"a":"NaN"}`, converting a numeric sentinel into a string
 * with a green "fixed!" toast (recon §3.3, B19/B20).
 *
 * `-Infinity` scans as two adjacent `Unknown` tokens (`-`, `Infinity`), so the sign is
 * absorbed into the span; otherwise the repair would leave a stray `-` behind.
 */
export const normalizeNonFiniteRule: RepairRule = {
  id: 'normalize-non-finite',
  confidence: 'heuristic',
  priority: 36,
  resolves: ['number/non-finite'],

  detect(ctx: RepairContext): readonly RepairProposal[] {
    const { namedKeys } = scanStructure(ctx.source, ctx.tokens);
    const sig = structuralTokens(ctx.source, ctx.tokens);
    const proposals: RepairProposal[] = [];

    for (let i = 0; i < sig.length; i += 1) {
      const token = sig[i];
      if (token === undefined || token.kind !== TokenKind.Unknown) continue;
      // Only a token the grammar reads as a key AND that is followed by a colon is
      // provably a property name; that is the one quote-unquoted-key claims. Anything
      // looser would skip the `Infinity` of `-Infinity`, which recovery also parks in key
      // position.
      if (namedKeys.has(token)) continue;

      const literal = textOf(ctx.source, token);
      const signed = literal.startsWith('-') || literal.startsWith('+');
      const bare = signed ? literal.slice(1) : literal;
      if (bare !== 'NaN' && bare !== 'Infinity') continue;

      // `+Infinity` scans as ONE token and `-Infinity` as two, so the sign arrives either
      // glued on or as the token before. Both have to end up inside the replaced span.
      const sign = signed ? undefined : signBefore(ctx.source, sig, i);
      const from = sign?.from ?? token.from;

      proposals.push({
        ruleId: 'normalize-non-finite',
        span: { from, to: token.to },
        edits: [{ from, to: token.to, insert: 'null' }],
        labelCode: 'fix.normalize-non-finite',
        labelParams: { literal: ctx.source.slice(from, token.to) },
        resolves: 'number/non-finite',
      });
    }

    return proposals;
  },
};

/** The `-` or `+` glued to the front of `Infinity`, if there is one. */
function signBefore(source: string, tokens: readonly Token[], index: number): Token | undefined {
  const token = tokens[index];
  const previous = tokens[index - 1];
  if (token === undefined || previous === undefined) return undefined;
  if (previous.kind !== TokenKind.Unknown) return undefined;
  if (previous.to !== token.from) return undefined;

  const text = source.slice(previous.from, previous.to);
  return text === '-' || text === '+' ? previous : undefined;
}
